/**
 * PageFormLayout - Reusable layout template for all create/edit form pages.
 * Provides consistent structure: header, left nav sidebar, sectioned form, sticky action bar.
 * Used by ~20 form pages across the application.
 *
 * Styled to match CoordioCore's form pattern — uses @coordio/ui components
 * (ShellHeader, PageContainer, FormSection, Button, InlineErrorPanel, LoadingSkeleton)
 * with a left-side section navigation that scrolls to sections on click.
 */

import { type ReactNode, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft as ArrowBackIcon } from 'lucide-react';

import { Button as UiButton, ShellHeader } from '@coordio/ui';
import {
  FormSection as UiFormSection,
  InlineErrorPanel,
  LoadingSkeleton,
  PageContainer,
} from '@coordio/ui/layout';

/** A single navigation item inside a group. */
interface NavItem {
  /** Must match a section id */
  id: string;
  label: string;
  icon?: ReactNode;
  /** Optional count badge shown on the right side */
  count?: number;
}

/** A group of navigation items with a header label. */
interface NavGroup {
  /** Group header — leave empty for a flat list */
  title?: string;
  items: NavItem[];
}

interface FormSection {
  id: string;
  title: string;
  description?: string;
  icon?: ReactNode;
  content: ReactNode;
}

interface FormActions {
  onSave: () => void;
  onCancel: () => void;
  saveLabel?: string;
  cancelLabel?: string;
  saving?: boolean;
  saveDisabled?: boolean;
}

interface PageFormLayoutProps {
  /** Page title */
  title: string;
  /** Optional subtitle / description */
  subtitle?: string;
  /** Back navigation config */
  backTo?: { label: string; href: string };
  /** Left sidebar navigation groups — omit for a simple form without sidebar */
  navGroups?: NavGroup[];
  /** Form sections */
  sections?: FormSection[];
  /** Form action buttons */
  actions: FormActions;
  /** Loading state */
  loading?: boolean;
  /** Error message */
  error?: string;
  /** Error dismiss handler */
  onErrorDismiss?: () => void;
  /** Content (alternative to sections for simple forms) */
  children?: ReactNode;
  /** Extra header actions (placed after the back button) */
  headerActions?: ReactNode;
  /** Test ID for the form wrapper */
  'data-testid'?: string;
}

/**
 * Reusable form page layout — CoordioCore pattern.
 *
 * Layout:
 * ┌──────────────────────────────────────────────────────────────┐
 * │ ShellHeader: title + description + Back to List + Save      │
 * ├──────────────────────────────────────────────────────────────┤
 * │ ┌───────────┐ ┌────────────────────────────────────────────┐│
 * │ │ GROUP 1   │ │ ⊕ Section Title                           ││
 * │ │ • Item 1  │ │   Section description                     ││
 * │ │ • Item 2  │ │ ┌──────────────────────────────────────┐   ││
 * │ │           │ │ │ Card with form fields                │   ││
 * │ │ GROUP 2   │ │ └──────────────────────────────────────┘   ││
 * │ │ • Item 3  │ │                                            ││
 * │ └───────────┘ └────────────────────────────────────────────┘│
 * └──────────────────────────────────────────────────────────────┘
 */
export const PageFormLayout = ({
  title,
  subtitle,
  backTo,
  navGroups,
  sections,
  actions,
  loading = false,
  error,
  onErrorDismiss,
  children,
  headerActions,
  'data-testid': testId,
}: PageFormLayoutProps) => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<string>(
    sections?.[0]?.id ?? ''
  );

  /** Switch to a section — CoordioCore uses tab-based show/hide, not scroll */
  const goToSection = useCallback((sectionId: string) => {
    setActiveSection(sectionId);
  }, []);

  if (loading) {
    return (
      <div data-testid={testId}>
        <ShellHeader title={title} description={subtitle} />
        <PageContainer>
          <LoadingSkeleton rows={6} data-testid={testId ? `${testId}-loading` : undefined} />
        </PageContainer>
      </div>
    );
  }

  const hasNav = navGroups && navGroups.length > 0;

  return (
    <div data-testid={testId}>
      <ShellHeader
        title={title}
        description={subtitle}
        actions={
          <div className="flex items-center gap-2">
            {backTo && (
              <UiButton
                variant="outline"
                onClick={() => navigate(backTo.href)}
                data-testid={testId ? `${testId}-back-btn` : undefined}
              >
                <ArrowBackIcon size={16} className="mr-1.5" />
                {backTo.label}
              </UiButton>
            )}
            {headerActions}
            {/* Primary save button in header — CoordioCore pattern */}
            <UiButton
              variant="default"
              onClick={actions.onSave}
              disabled={actions.saving || actions.saveDisabled}
              data-testid={testId ? `${testId}-save-btn` : undefined}
            >
              {actions.saving ? (
                <>
                  <span className="mr-1.5 inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Saving…
                </>
              ) : (actions.saveLabel || 'Create')}
            </UiButton>
          </div>
        }
      />

      <PageContainer>
        {/* Error banner */}
        {error && (
          <InlineErrorPanel
            title="Error"
            message={error}
            data-testid={testId ? `${testId}-error` : undefined}
            action={onErrorDismiss ? <UiButton variant="ghost" size="sm" onClick={onErrorDismiss}>Dismiss</UiButton> : undefined}
          />
        )}

        {/* Main layout: CSS grid sidebar + content — CoordioCore pattern */}
        <div className={hasNav ? 'grid gap-[1rem]' : ''} style={hasNav ? { gridTemplateColumns: '200px minmax(0, 1fr)' } : undefined}>

          {/* Left sidebar navigation — exact CoordioCore SectionSideNav CSS */}
          {hasNav && (
            <aside
              className="section-nav sticky top-1 self-start flex flex-col gap-[3px] rounded-[0.75rem] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-2"
              style={{ width: 200 }}
              aria-label="Form sections"
            >
              {navGroups.map((group, gi) => (
                <div key={group.title ?? `g${gi}`}>
                  {group.title && (
                    <div
                      className="px-[0.625rem] pt-[0.65rem] pb-[0.35rem] text-[0.625rem] font-semibold uppercase tracking-[0.06em] text-[var(--color-text-secondary)]"
                    >
                      {group.title}
                    </div>
                  )}
                  {group.items.map((item) => {
                    const isActive = activeSection === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => goToSection(item.id)}
                        data-section-nav-target={item.id}
                        className={`flex w-full items-center gap-[0.625rem] rounded-[0.4375rem] px-[0.65rem] py-[0.5rem] text-[0.8125rem] font-medium transition-colors ${
                          isActive
                            ? 'bg-[var(--color-accent-subtle)] text-[var(--color-accent-primary)]'
                            : 'bg-transparent text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-inset)] hover:text-[var(--color-text-primary)]'
                        }`}
                        style={{ minHeight: '2.375rem' }}
                      >
                        {item.icon && <span className="flex-shrink-0 opacity-85" style={{ width: '0.875rem', height: '0.875rem' }}>{item.icon}</span>}
                        <span className="flex-1 overflow-hidden text-ellipsis text-left">{item.label}</span>
                        {typeof item.count === 'number' && (
                          <span
                            className={`ml-auto inline-flex items-center justify-center rounded-full text-[0.65625rem] font-medium tabular-nums ${
                              isActive
                                ? 'bg-[var(--color-accent-primary)] text-[var(--color-text-on-accent)]'
                                : 'bg-[var(--color-bg-inset)] text-[var(--color-text-secondary)]'
                            }`}
                            style={{ minWidth: '1.2rem', padding: '1px 0.4rem' }}
                          >
                            {item.count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </aside>
          )}

          {/* Form section panels — CoordioCore tab-based: only active section renders */}
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            {sections ? (
              sections
                .filter((section) => section.id === activeSection)
                .map((section) => (
                  <section
                    key={section.id}
                    className="flex flex-col gap-3 rounded-[1rem] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-4"
                  >
                    {/* Panel head — CoordioCore: rounded-square icon + h2 + description */}
                    <div className="flex items-center gap-3 pb-3 border-b border-[var(--color-border-default)]">
                      {section.icon && (
                        <div
                          className="flex shrink-0 items-center justify-center bg-[var(--color-accent-subtle)] text-[var(--color-accent-primary)]"
                          style={{ width: '2rem', height: '2rem', borderRadius: '0.55rem' }}
                        >
                          {section.icon}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h2 className="text-base font-semibold leading-tight m-0">{section.title}</h2>
                        {section.description && (
                          <p className="mt-0.5 text-sm text-[var(--color-text-secondary)] m-0 leading-snug">{section.description}</p>
                        )}
                      </div>
                    </div>
                    {/* Section content — FormSection cards go here */}
                    {section.content}
                  </section>
                ))
            ) : (
              <UiFormSection title={title}>
                {children}
              </UiFormSection>
            )}
          </div>
        </div>
      </PageContainer>
    </div>
  );
};

export default PageFormLayout;
